import React from "react";
import { Box, Typography } from "@mui/material";

import MHFooter from "../components/Layout/MHFooter";
import MHButton from "../components/Common/Button/MHButton";
import {
  FounderSpotlight,
  LeadershipGrid,
} from "../components/Team/TeamMembers";
import useTitle from "../hooks/use-title";

type Props = {};

const Team = (props: Props) => {
  useTitle("Our Team");

  return (
    <React.Fragment>
      {/* Hero */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          backgroundColor: "#194049",
          pt: { xs: 14, md: 18 },
          pb: { xs: 12, md: 16 },
          px: 2,
        }}
      >
        {[900, 680, 460, 240].map((size) => (
          <Box
            key={size}
            aria-hidden="true"
            sx={{
              position: "absolute",
              width: size,
              height: size,
              borderRadius: "50%",
              border: "1px solid rgba(160, 189, 210, 0.18)",
              right: { xs: -size / 2, md: -size / 3 },
              top: "50%",
              transform: "translateY(-50%)",
            }}
          />
        ))}

        <Box
          sx={{ position: "relative", maxWidth: 1100, mx: "auto" }}
          className="text-center md:text-left"
        >
          <Typography
            variant="caption"
            fontSize={{ xs: "12px", sm: "14px" }}
            letterSpacing="0.1em"
            className="uppercase"
            sx={{ color: "#A0BDD2" }}
          >
            Our Team
          </Typography>

          <Typography
            variant="h1"
            fontSize={{ xs: "44px", sm: "64px", md: "88px" }}
            lineHeight={{ xs: "100%", md: "92%" }}
            letterSpacing="-0.065em"
            className="pt-6"
            sx={{ color: "#FFFFFF", maxWidth: 820 }}
          >
            The people powering Africa's{" "}
            <Box
              component="span"
              sx={{
                color: "#EDD5B1",
                font: "inherit",
                letterSpacing: "inherit",
              }}
            >
              care revolution
            </Box>
          </Typography>

          <Typography
            variant="body1"
            fontSize={{ xs: "15px", md: "17px" }}
            lineHeight="185%"
            letterSpacing="0.04em"
            className="pt-8 mx-auto md:mx-0"
            sx={{ color: "#C7D8E4", maxWidth: 620 }}
          >
            We are building the care infrastructure necessary for business and
            economic growth, bridging the care gap to foster gender equity, and
            formalizing the care economy for a sustainable society.
          </Typography>
        </Box>
      </Box>

      {/* Leadership */}
      <Box className="bg-lilac-100" sx={{ py: { xs: 10, md: 14 }, px: 2 }}>
        <Box sx={{ maxWidth: 1100, mx: "auto" }}>
          <FounderSpotlight />

          <Box sx={{ mt: { xs: 12, md: 16 }, mb: { xs: 5, md: 6 } }}>
            <Typography
              variant="h3"
              color="primary"
              fontSize={{ xs: "30px", sm: "40px", md: "43px" }}
              lineHeight="120%"
              letterSpacing={{ xs: "-0.04em", sm: "-0.06em" }}
            >
              Leadership
            </Typography>
          </Box>

          <LeadershipGrid />
        </Box>
      </Box>

      {/* Call to action */}
      <Box
        className="bg-lilac-300 text-center"
        sx={{ py: { xs: 10, md: 12 }, px: 2 }}
      >
        <Typography
          variant="caption"
          color="primary"
          fontSize={{ xs: "12px", sm: "14px" }}
          letterSpacing="0.1em"
          className="uppercase"
        >
          Join the movement
        </Typography>
        <Typography
          variant="h3"
          color="primary"
          fontSize={{ xs: "32px", sm: "40px", md: "50px" }}
          lineHeight="110%"
          letterSpacing="-0.05em"
          className="pt-4 mx-auto"
          sx={{ maxWidth: 640 }}
        >
          Care is infrastructure. Let's build it together.
        </Typography>

        <Box className="flex flex-col sm:flex-row justify-center gap-4 pt-10">
          <MHButton to="/partner" sx={{ minWidth: 200 }}>
            Partner With Us
          </MHButton>
          <MHButton
            to="/about"
            variant="outlined"
            color="secondary"
            sx={{ minWidth: 200 }}
          >
            About Caring Africa
          </MHButton>
        </Box>
      </Box>

      <MHFooter />
    </React.Fragment>
  );
};

export default Team;
